/**
 * NAT-2481 — standing rule (Antonio, 28 Sep 2026): every chart shows its value on hover and on
 * keyboard focus. One primitive for every hand-made chart (no chart library since NAT-1788):
 *
 * - `ChartHoverColumns` lays transparent, focusable columns over a column or line chart. Every
 *   chart here draws its periods in equal slots across the drawing's full width, so a CSS grid of
 *   the same count lines up with the marks whatever the chart's own coordinate space.
 * - `ChartHoverItem` wraps one row of a horizontal bar list.
 *
 * Both live in a `ChartHoverGroup`: a list with ONE tab stop (roving tabindex), entered on the
 * latest item; ← → (and ↑ ↓) move, Home and End jump, Escape closes the tooltip. The tooltip is
 * controlled — opened by hover, by focus and by a tap (touch has no hover) — so keyboard, pointer
 * and touch behave alike, in jsdom too. One chart tooltip shows on the page at a time.
 */
import type { SxProps, Theme } from '@mui/material/styles';

import {
  useId,
  useRef,
  useMemo,
  useState,
  useEffect,
  useContext,
  useCallback,
  createContext,
  useLayoutEffect,
  useSyncExternalStore,
  type ReactNode,
  type ElementType,
  type KeyboardEvent,
} from 'react';

import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

// ----------------------------------------------------------------------

export type ChartHoverLine = {
  /** The series: «Periodo», «Anno precedente», «60+ giorni», «DSO». */
  label: string;
  /** Already formatted with the chart's own formatter. */
  value: string;
  /**
   * The series' colour in the drawing: a palette token, or — for a chart colour with no mode-aware
   * token — the light and dark values, switched by the theme's `data-color-scheme`.
   */
  color?: string | { light: string; dark: string };
};

export type ChartHoverDatum = {
  /**
   * The item's identity: unique and stable within its group, and the same as its React `key`.
   * Hover, focus and the tab stop follow it when the data reorders.
   */
  key: string;
  /** The period or category: «ago 26», «Lombardia», «10–15 €». */
  title: string;
  lines: ChartHoverLine[];
  /** «Parziale: giorni 1–27», «Ricostruito», «Mese in corso». */
  note?: string;
};

/** The item's accessible name: the tooltip's content as one sentence. */
export const chartHoverText = (d: ChartHoverDatum) =>
  [`${d.title}: ${d.lines.map((l) => `${l.label} ${l.value}`).join(', ')}`, d.note]
    .filter(Boolean)
    .join('. ');

/**
 * A series' key in the tooltip. Its ring is the tooltip's own text colour, softened, so any series
 * colour reads against the tooltip's background — `text.primary` is that background's own grey in
 * light mode, and `text.disabled` all but vanishes on it in dark mode.
 */
function Swatch({ color }: { color: NonNullable<ChartHoverLine['color']> }) {
  return (
    <Box
      sx={{
        width: 10,
        height: 10,
        flexShrink: 0,
        borderRadius: 0.5,
        boxShadow: '0 0 0 1px color-mix(in srgb, currentColor 70%, transparent)',
        ...(typeof color === 'string'
          ? { bgcolor: color }
          : { bgcolor: color.light, '[data-color-scheme="dark"] &': { bgcolor: color.dark } }),
      }}
    />
  );
}

export function ChartTooltipContent({ datum }: { datum: ChartHoverDatum }) {
  return (
    <Box sx={{ py: 0.5, minWidth: 120 }}>
      <Typography variant="subtitle2">{datum.title}</Typography>
      {datum.lines.map((line) => (
        <Box key={line.label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {line.color && <Swatch color={line.color} />}
          <Typography variant="caption">{line.label}</Typography>
          <Typography variant="caption" sx={{ ml: 'auto', fontWeight: 'fontWeightSemiBold' }}>
            {line.value}
          </Typography>
        </Box>
      ))}
      {datum.note && (
        <Typography variant="caption" sx={{ display: 'block', opacity: 0.8 }}>
          {datum.note}
        </Typography>
      )}
    </Box>
  );
}

// ----------------------------------------------------------------------

/**
 * The one chart tooltip on the page. Hover and focus are two separate states: the hovered item
 * wins, and the tooltip falls back to the focused one — so a mouse passing over (and leaving) a
 * focused item does not close its tooltip, and a focused item in one chart and a hovered column in
 * another never show two. Escape dismisses whichever is showing (WCAG 1.4.13) until the next hover
 * or focus.
 *
 * Both are keyed on the item's identity (`datum.key`), not its position, so data that reorders
 * under a reader moves their tooltip and focus with the item instead of handing them to whichever
 * item lands on the old index.
 *
 * The store is module state, global to the page, not to a group: that is what makes it one tooltip
 * per page. It relies on items unmounting — each takes its own hover and focus with it — so no
 * stale spot outlives a chart. In tests that is RTL's automatic cleanup after each test, which
 * unmounts every item; a test that renders without that cleanup would carry the spot over.
 * Every item listens to every change; `useSyncExternalStore` compares a boolean, so only the
 * items whose open state flips re-render, and scoping listeners per group would save nothing real.
 */
type Spot = { group: string; key: string };

let hovered: Spot | null = null;
let focused: (Spot & { shown: boolean }) | null = null;
const listeners = new Set<() => void>();

const at = (spot: Spot | null, group: string, key: string) =>
  !!spot && spot.group === group && spot.key === key;

const change = (next: () => void) => {
  next();
  listeners.forEach((listener) => listener());
};

const hoverStore = {
  subscribe: (listener: () => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  isOpen: (group: string, key: string) =>
    hovered ? at(hovered, group, key) : !!focused?.shown && at(focused, group, key),
  /** The key of the item holding keyboard focus in this group, if any. */
  focusedIn: (group: string) => (focused && focused.group === group ? focused.key : null),
  enter: (group: string, key: string) =>
    change(() => {
      hovered = { group, key };
    }),
  leave: (group: string, key: string) =>
    change(() => {
      if (at(hovered, group, key)) hovered = null;
    }),
  focus: (group: string, key: string) =>
    change(() => {
      focused = { group, key, shown: true };
      // Deliberate (NAT-2481 review, round 2 — "hover must always win" was declined): Tab or an
      // arrow key moved on, so a hover left resting on ANOTHER item is older news than the
      // reader's latest move, and the tooltip follows the keyboard. The pointer takes it back on
      // its next enter.
      if (!at(hovered, group, key)) hovered = null;
    }),
  blur: (group: string, key: string) =>
    change(() => {
      if (at(focused, group, key)) focused = null;
    }),
  dismiss: () =>
    change(() => {
      hovered = null;
      if (focused) focused = { ...focused, shown: false };
    }),
};

/** The roving tab stop: by identity, with its last known position to clamp to if it goes. */
type Active = { key: string; index: number };

type GroupState = {
  id: string;
  /** The index that is the tab stop now. */
  active: number;
  /**
   * Items repeating an earlier item's key. Hover and focus are keyed on identity, so the first
   * match shows the tooltip and these stay closed: one tooltip, never two.
   */
  shadowed: number[];
  register: (index: number, key: string, el: HTMLElement | null) => void;
  setActive: (active: Active) => void;
  onKeyDown: (event: KeyboardEvent<HTMLElement>, index: number) => void;
};

const GroupContext = createContext<GroupState | null>(null);

export function ChartHoverGroup({
  label,
  count,
  children,
  sx,
}: {
  /** The chart's accessible name. */
  label: string;
  count: number;
  children: ReactNode;
  sx?: SxProps<Theme>;
}) {
  const id = useId();
  const items = useRef<({ key: string; el: HTMLElement } | null)[]>([]);
  const last = Math.max(count - 1, 0);
  const [active, setActive] = useState<Active | null>(null);
  // Entered on the latest item until the reader moves; clamped when the data shrinks. When the item
  // moves, the effect below re-states its index, so this follows it.
  const safeActive = active === null ? last : Math.min(active.index, last);
  const [shadowed, setShadowed] = useState<number[]>([]);
  const warnedDuplicate = useRef(false);

  const register = useCallback((index: number, key: string, el: HTMLElement | null) => {
    items.current[index] = el ? { key, el } : null;
  }, []);

  // Runs after every commit, once the items' refs are in. If the item holding keyboard focus left
  // the DOM (the data shrank, or it moved and the browser dropped focus on the move), focus falls
  // to <body> and the next Tab would skip past the chart: put it back — on the same item if it is
  // still here, else on the clamped tab stop. Only when focus was in this group and has gone
  // nowhere else, so it never steals focus from the rest of the page.
  useLayoutEffect(() => {
    items.current.length = count;
    const key = hoverStore.focusedIn(id);
    if (key === null) return;
    const current = document.activeElement;
    if (current && current !== document.body) return;
    const target = items.current.find((it) => it?.key === key) ?? items.current[safeActive];
    target?.el.focus();
  });

  // Once every item's ref is in, after the data changed or the reader moved. `datum.key` should be
  // unique, but a slug or SKU is not unique-indexed, so a repeated key must degrade quietly:
  // - the items repeating an earlier key are shadowed (see GroupState);
  // - the roving tab stop follows its item when the data reorders: its index is re-stated only when
  //   the stop no longer holds an item with its key, and then resolved here, once, to the first
  //   index holding it. Two items sharing a key must never each claim the stop in turn: that loops
  //   React until it throws "Maximum update depth exceeded".
  useLayoutEffect(() => {
    const seen = new Set<string>();
    const repeats: number[] = [];
    items.current.forEach((it, index) => {
      if (!it) return;
      if (seen.has(it.key)) repeats.push(index);
      seen.add(it.key);
    });
    setShadowed((prev) => (prev.join() === repeats.join() ? prev : repeats));
    if (import.meta.env.DEV && repeats.length && !warnedDuplicate.current) {
      warnedDuplicate.current = true;
      console.warn(`ChartHoverGroup "${label}": duplicate datum.key — keys must be unique.`);
    }

    if (active === null || items.current[safeActive]?.key === active.key) return;
    const index = items.current.findIndex((it) => it?.key === active.key);
    if (index !== -1) setActive({ key: active.key, index });
  }, [children, active, safeActive, label]);

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>, index: number) => {
      const moves: Record<string, number> = {
        ArrowLeft: index - 1,
        ArrowUp: index - 1,
        ArrowRight: index + 1,
        ArrowDown: index + 1,
        Home: 0,
        End: count - 1,
      };
      if (!(event.key in moves)) return;
      event.preventDefault();
      const next = Math.min(Math.max(moves[event.key], 0), Math.max(count - 1, 0));
      const target = items.current[next];
      if (!target) return;
      setActive({ key: target.key, index: next });
      target.el.focus();
    },
    [count],
  );

  const value = useMemo(
    () => ({
      id,
      active: safeActive,
      shadowed,
      register,
      setActive,
      onKeyDown,
    }),
    [id, safeActive, shadowed, register, onKeyDown],
  );

  return (
    <GroupContext.Provider value={value}>
      <Box role="list" aria-label={label} sx={sx}>
        {children}
      </Box>
    </GroupContext.Provider>
  );
}

/**
 * One item of a group. With `href` (and `component`, e.g. `RouterLink`) the item is itself the
 * link — one element that is the tab stop, carries the arrow keys and the tooltip, and navigates
 * on Enter or click — wrapped in a plain list item, so no interactive element nests in another.
 */
export function ChartHoverItem({
  index,
  datum,
  children,
  component,
  href,
  elementProps,
  sx,
}: {
  index: number;
  datum: ChartHoverDatum;
  children?: ReactNode;
  /**
   * Attributes for the rendered element itself: the geometry of an SVG `component` (`d`, `cx`, `r`),
   * or a `data-*` hook. The item's own props (role, tabIndex, handlers) win over these.
   */
  elementProps?: Record<string, unknown>;
  /** The element to render, for a linked row: `RouterLink`, or `'a'` (the default with `href`). */
  component?: ElementType;
  href?: string;
  sx?: SxProps<Theme>;
}) {
  const group = useContext(GroupContext);
  if (!group) throw new Error('ChartHoverItem must sit inside a ChartHoverGroup');
  const { id, active, shadowed, register, setActive, onKeyDown } = group;
  const { key } = datum;
  const spotOpen = useSyncExternalStore(
    hoverStore.subscribe,
    () => hoverStore.isOpen(id, key),
    () => false,
  );
  const open = spotOpen && !shadowed.includes(index);
  // An item that goes away takes its hover and focus with it. Keyed on identity: an item that
  // only moves keeps both.
  useEffect(
    () => () => {
      hoverStore.leave(id, key);
      hoverStore.blur(id, key);
    },
    [id, key],
  );

  // The item moved (the data reordered). Before paint, so nothing flashes: the pointer did not, so
  // this item is no longer the one under it — drop its hover, and the next pointer move enters
  // whatever is there now. (If it is the roving tab stop, the group re-states its new index.)
  const lastIndex = useRef(index);
  useLayoutEffect(() => {
    if (lastIndex.current === index) return;
    lastIndex.current = index;
    hoverStore.leave(id, key);
  }, [id, key, index]);

  const linked = href !== undefined;
  const item = (
    <Tooltip
      title={<ChartTooltipContent datum={datum} />}
      open={open}
      // MUI calls this on Escape anywhere on the page while the tooltip is open.
      onClose={hoverStore.dismiss}
      placement="top"
      arrow
      describeChild
      disableInteractive
      // Controlled: hover, focus and tap below decide; MUI's own listeners stay out of it.
      disableHoverListener
      disableFocusListener
      disableTouchListener
      // Sweeping across columns: the old tooltip goes at once, so two never stack.
      slotProps={{ transition: { timeout: { enter: 150, exit: 0 } } }}
    >
      <Box
        {...elementProps}
        component={component ?? (linked ? 'a' : 'div')}
        href={href}
        role={linked ? undefined : 'listitem'}
        tabIndex={active === index ? 0 : -1}
        aria-label={chartHoverText(datum)}
        data-chart-hover-item={key}
        ref={(el: HTMLElement | null) => register(index, key, el)}
        onMouseEnter={() => hoverStore.enter(id, key)}
        onMouseLeave={() => hoverStore.leave(id, key)}
        // A tap: touch has no hover, and a tap on a focused item fires no focus event.
        onClick={() => hoverStore.enter(id, key)}
        onFocus={() => {
          setActive({ key, index });
          hoverStore.focus(id, key);
        }}
        onBlur={() => hoverStore.blur(id, key)}
        onKeyDown={(event: KeyboardEvent<HTMLElement>) => onKeyDown(event, index)}
        sx={[
          (theme) => ({
            outline: 'none',
            cursor: linked ? 'pointer' : 'default',
            ...(linked && { display: 'block', color: 'inherit', textDecoration: 'none' }),
            // theme.vars is absent without the app's CssVarsProvider (a bare test render).
            '&:focus-visible': {
              boxShadow: `0 0 0 2px ${(theme.vars ?? theme).palette.primary.main}`,
            },
            '&:hover, &:focus-visible': { bgcolor: 'action.hover' },
          }),
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        {children}
      </Box>
    </Tooltip>
  );
  return linked ? <Box role="listitem">{item}</Box> : item;
}

/** Transparent columns over a column or line chart; the parent must be `position: relative`. */
export function ChartHoverColumns({
  label,
  data,
  sx,
}: {
  label: string;
  data: ChartHoverDatum[];
  /** To inset the overlay onto the plot area when the drawing has axis gutters. */
  sx?: SxProps<Theme>;
}) {
  return (
    <ChartHoverGroup
      label={label}
      count={data.length}
      sx={[
        {
          position: 'absolute',
          inset: 0,
          display: 'grid',
          gridTemplateColumns: `repeat(${Math.max(data.length, 1)}, minmax(0, 1fr))`,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {data.map((datum, index) => (
        <ChartHoverItem
          // A repeated datum.key gets its own React key, so React never drops or doubles a column.
          key={
            data.findIndex((d) => d.key === datum.key) === index
              ? datum.key
              : `${datum.key}#${index}`
          }
          index={index}
          datum={datum}
          sx={{ borderRadius: 0.5 }}
        />
      ))}
    </ChartHoverGroup>
  );
}
